import React, { ErrorInfo, ReactNode } from "react"
import { Box, Button, Flex, Heading, Text, VStack, Icon, Container } from "@chakra-ui/react"
import { WarningIcon, RepeatIcon } from "@chakra-ui/icons"

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Flex minHeight="100vh" alignItems="center" justifyContent="center" bg="gray.50">
          <Container maxWidth={{ base: "90%", sm: "80%", md: "md" }} px={0}>
            <Box 
              width="full" 
              bg="white" 
              borderRadius={{ base: "lg", md: "xl" }} 
              boxShadow={{ base: "lg", md: "2xl" }} 
              p={{ base: 6, md: 8 }}
            >
              <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                <Flex alignItems="center" justifyContent="center" flexDirection={{ base: "column", md: "row" }}>
                  <Icon 
                    as={WarningIcon} 
                    w={{ base: 8, md: 10 }} 
                    h={{ base: 8, md: 10 }} 
                    color="red.500" 
                    mb={{ base: 2, md: 0 }}
                    mr={{ base: 0, md: 3 }}
                  />
                  <Heading 
                    size={{ base: "lg", md: "xl" }} 
                    color="red.500" 
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Oops! Algo deu errado
                  </Heading>
                </Flex>
                <Text 
                  fontSize={{ base: "md", md: "lg" }} 
                  color="gray.600" 
                  textAlign="center"
                >
                  Desculpe pelo inconveniente. Ocorreu um erro inesperado.
                </Text>
                <Text 
                  fontSize={{ base: "sm", md: "md" }} 
                  color="gray.500" 
                  textAlign="center"
                >
                  Por favor, tente novamente. Se o problema persistir, entre em contato com o administrador do sistema.
                </Text>
                <Button
                  leftIcon={<RepeatIcon />}
                  colorScheme="blue"
                  size={{ base: "md", md: "lg" }}
                  onClick={this.handleRetry}
                  mt={{ base: 2, md: 4 }}
                  _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                  width={{ base: "full", md: "auto" }}
                  alignSelf="center"
                >
                  Tentar novamente
                </Button>
              </VStack>
            </Box>
          </Container>
        </Flex>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary